import { useEffect, useMemo, useState } from 'react';

import type { Country } from '../../../../domain/country';
import { Preference } from '../../../../domain/preferences';
import { useCountryPreferencesMutation } from '../../../../features/preferences/useCountryPreferencesMutation';
import { useCountryPreferencesQuery } from '../../../../features/preferences/useCountryPreferencesQuery';
import { useUserQuery } from '../../../../features/user/useUserQuery';
import { arraysAreEqual } from '../../../../services/array';
import { CountryService, WORLDWIDE_COUNTRY_ID } from '../../../../services/country/country';
import { usePreferencesTracking } from '../usePreferencesTracking';

export const useCountriesPreferences = (onClose: () => void) => {
  const { handleTrackApplyPreferences, handleTrackRemoveCountry, handleTrackAddCountry } = usePreferencesTracking();
  const { availableCountries = [] } = useUserQuery().data || {};
  const { data: initiallySelectedCountriesIds, isLoading: isInitialCountriesLoading } = useCountryPreferencesQuery();
  const { mutate: mutateCountryPreferences, isPending: isMutationLoading } = useCountryPreferencesMutation(onClose);

  const isWorldwideAvailable = CountryService.isWorldwideIncluded(availableCountries);

  const countries = useMemo(() => CountryService.getCountriesByIds(availableCountries), [availableCountries]);

  const isInitiallyWorldwideSelected = useMemo(
    () => CountryService.isWorldwideIncluded(initiallySelectedCountriesIds || []),
    [initiallySelectedCountriesIds]
  );

  const initiallySelectedCountries: Country[] = useMemo(
    () =>
      initiallySelectedCountriesIds && !isInitiallyWorldwideSelected
        ? CountryService.getCountriesByIds(
            initiallySelectedCountriesIds.filter((id) => availableCountries.includes(id))
          )
        : [],
    [initiallySelectedCountriesIds, isInitiallyWorldwideSelected]
  );

  const initiallyNotSelectedCountries: Country[] = useMemo(
    () =>
      initiallySelectedCountriesIds && !isInitiallyWorldwideSelected
        ? countries.filter((country) => !initiallySelectedCountriesIds.includes(country.id))
        : countries,
    [initiallySelectedCountriesIds, countries, isInitiallyWorldwideSelected]
  );

  const getAvailableCountryIds = (countryIds: number[]) => {
    return countryIds.filter((id) => id !== WORLDWIDE_COUNTRY_ID && availableCountries.includes(id));
  };

  const createSelectedCountryIdsSet = (countryIds?: number[]) => {
    return new Set(getAvailableCountryIds(countryIds || []));
  };

  const [isWorldwideSelected, setIsWorldwideSelected] = useState<boolean>(() =>
    initiallySelectedCountriesIds
      ? CountryService.isWorldwideIncluded(initiallySelectedCountriesIds)
      : isWorldwideAvailable
  );
  const [selectedCountryIdsSet, setSelectedCountryIdsSet] = useState<Set<number>>(
    createSelectedCountryIdsSet(initiallySelectedCountriesIds)
  );

  useEffect(() => {
    if (initiallySelectedCountriesIds) {
      setIsWorldwideSelected(isInitiallyWorldwideSelected);
      setSelectedCountryIdsSet(
        isInitiallyWorldwideSelected ? new Set() : createSelectedCountryIdsSet(initiallySelectedCountriesIds)
      );
    }
  }, [initiallySelectedCountriesIds]);

  const handleClickOnWorldWide = () => {
    setIsWorldwideSelected(true);
    setSelectedCountryIdsSet(new Set());
  };

  const handleClickOnSpecificCountries = () => {
    setIsWorldwideSelected(false);
  };

  const handleClickOnCountry = (country: Country) => {
    setSelectedCountryIdsSet((prevSelectedCountryIdsSet) => {
      const newSelectedCountryIdsSet = new Set(prevSelectedCountryIdsSet);
      if (newSelectedCountryIdsSet.has(country.id)) {
        handleTrackRemoveCountry(country.text, prevSelectedCountryIdsSet.size - 1);
        newSelectedCountryIdsSet.delete(country.id);
      } else {
        handleTrackAddCountry(country.text, prevSelectedCountryIdsSet.size + 1);
        newSelectedCountryIdsSet.add(country.id);
      }
      return newSelectedCountryIdsSet;
    });
  };

  const handleSaveCountries = () => {
    handleTrackApplyPreferences({
      selectedPreference: Preference.countries,
      countriesCount: isWorldwideSelected ? 1 : selectedCountryIdsSet.size,
    });
    if (isWorldwideSelected) {
      mutateCountryPreferences([WORLDWIDE_COUNTRY_ID]);
    } else {
      mutateCountryPreferences(Array.from(selectedCountryIdsSet));
    }
  };

  const isNoChangeInValues = useMemo(() => {
    const areAllInitiallySelectedCountriesAvailable = initiallySelectedCountriesIds?.every((id) =>
      availableCountries.includes(id)
    );
    if (!areAllInitiallySelectedCountriesAvailable) {
      return false;
    }
    return isWorldwideSelected
      ? CountryService.isWorldwideIncluded(initiallySelectedCountriesIds || []) === isWorldwideSelected
      : arraysAreEqual(Array.from(selectedCountryIdsSet), getAvailableCountryIds(initiallySelectedCountriesIds || []));
  }, [initiallySelectedCountriesIds, isWorldwideSelected, selectedCountryIdsSet, availableCountries]);

  const isSaveDisabled = useMemo(
    () => isInitialCountriesLoading || (!isWorldwideSelected && selectedCountryIdsSet.size === 0) || isNoChangeInValues,
    [isInitialCountriesLoading, isWorldwideSelected, selectedCountryIdsSet.size, isNoChangeInValues]
  );

  return {
    isWorldwideAvailable,
    isWorldwideSelected,
    handleClickOnWorldWide,
    handleClickOnSpecificCountries,
    handleClickOnCountry,
    initiallyNotSelectedCountries,
    initiallySelectedCountries,
    selectedCountryIdsSet,
    handleSaveCountries,
    isSaveDisabled,
    isNoChangeInValues,
    isLoading: isInitialCountriesLoading,
    isMutationLoading,
  };
};
